import {
  DATE_TIME_PICKER_MAX_MINUTES,
  DATE_TIME_PICKER_STEP,
} from "./dateTimePickerConstants";
import { buildBlockedDateSet, toDateKey } from "./dateUtils";

const normalizeAddressValue = (value) =>
  `${value ?? ""}`.toLowerCase().replace(/\s+/g, " ").trim();

export const buildLocationCoords = (latitude, longitude) => {
  const lat = typeof latitude === "number" ? latitude : Number(latitude);
  const lng = typeof longitude === "number" ? longitude : Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
};

const WORKING_HOURS_REGEX = /(\d{1,2}):(\d{2})\s*[–-]\s*(\d{1,2}):(\d{2})/;
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const parseTimeToMinutes = (hoursRaw, minutesRaw) => {
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const clampWorkingMinutes = (minutes) => {
  const clamped = Math.max(0, Math.min(minutes, DATE_TIME_PICKER_MAX_MINUTES));
  return Math.round(clamped / DATE_TIME_PICKER_STEP) * DATE_TIME_PICKER_STEP;
};

export const parseWeeklyOperatingHours = (value) => {
  if (!value) return null;
  const normalized = String(value).replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  const hasWeekday = WEEKDAY_LABELS.some((label) => normalized.includes(label));
  if (!hasWeekday) return null;

  const dayIndex = new Map(WEEKDAY_LABELS.map((label, index) => [label, index]));
  const result = WEEKDAY_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: [],
    }),
    {}
  );

  normalized
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const match = part.match(
        /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun)(?:-(Mon|Tue|Wed|Thu|Fri|Sat|Sun))?\s+(.+)$/
      );
      if (!match) return;
      const startLabel = match[1];
      const endLabel = match[2] || match[1];
      const rangesText = match[3] || "";
      const ranges = rangesText
        .split(",")
        .map((range) => range.trim())
        .filter(Boolean)
        .map((range) => {
          const rangeMatch = range.match(
            /(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/
          );
          if (!rangeMatch) return null;
          const startRaw = parseTimeToMinutes(rangeMatch[1], rangeMatch[2]);
          const endRaw = parseTimeToMinutes(rangeMatch[3], rangeMatch[4]);
          if (startRaw == null || endRaw == null || endRaw <= startRaw) return null;
          const startMinutes = clampWorkingMinutes(startRaw);
          const endMinutes = clampWorkingMinutes(endRaw);
          if (endMinutes <= startMinutes) return null;
          return { startMinutes, endMinutes };
        })
        .filter(Boolean);

      if (!ranges.length) return;
      const startIndex = dayIndex.get(startLabel);
      const endIndex = dayIndex.get(endLabel);
      if (startIndex == null || endIndex == null || startIndex > endIndex) return;
      for (let i = startIndex; i <= endIndex; i += 1) {
        const key = WEEKDAY_KEYS[i];
        result[key] = [...(result[key] || []), ...ranges];
      }
    });

  return result;
};

const getWeekdayKey = (date) => {
  const dayIndex = date.getDay();
  const mondayBased = (dayIndex + 6) % 7;
  return WEEKDAY_KEYS[mondayBased];
};

export const getWorkingWindowForDate = (operatingHours, date) => {
  if (!(date instanceof Date)) {
    return {
      minMinutes: 0,
      maxMinutes: DATE_TIME_PICKER_MAX_MINUTES,
      hasHours: true,
    };
  }

  const weekly = parseWeeklyOperatingHours(operatingHours);
  if (!weekly) {
    const legacy = parseWorkingHours(operatingHours);
    if (!legacy) {
      return {
        minMinutes: 0,
        maxMinutes: DATE_TIME_PICKER_MAX_MINUTES,
        hasHours: true,
      };
    }
    return {
      minMinutes: legacy.startMinutes,
      maxMinutes: legacy.endMinutes,
      hasHours: true,
    };
  }

  const key = getWeekdayKey(date);
  const ranges = weekly[key] || [];
  if (!ranges.length) {
    return { minMinutes: 0, maxMinutes: 0, hasHours: false };
  }
  const minMinutes = Math.min(...ranges.map((range) => range.startMinutes));
  const maxMinutes = Math.max(...ranges.map((range) => range.endMinutes));
  return {
    minMinutes,
    maxMinutes,
    hasHours: maxMinutes > minMinutes,
  };
};

export const getClosedWeekdays = (operatingHours) => {
  const weekly = parseWeeklyOperatingHours(operatingHours);
  if (!weekly) return [];
  return WEEKDAY_KEYS.reduce((acc, key, index) => {
    const ranges = weekly[key] || [];
    if (!ranges.length) acc.push(index);
    return acc;
  }, []);
};

export const getBlockedDateSet = (blockedDates) => buildBlockedDateSet(blockedDates);

export const isBlockedDate = (blockedDates, date) => {
  const key = toDateKey(date);
  if (!key) return false;
  return getBlockedDateSet(blockedDates).has(key);
};

export const parseWorkingHours = (value) => {
  if (!value) return null;
  const match = `${value}`.match(WORKING_HOURS_REGEX);
  if (!match) return null;
  const startRaw = Number(match[1]) * 60 + Number(match[2]);
  const endRaw = Number(match[3]) * 60 + Number(match[4]);
  if (!Number.isFinite(startRaw) || !Number.isFinite(endRaw) || endRaw <= startRaw) {
    return null;
  }
  const clampedStart = Math.max(0, Math.min(startRaw, DATE_TIME_PICKER_MAX_MINUTES - DATE_TIME_PICKER_STEP));
  const clampedEnd = Math.max(
    clampedStart + DATE_TIME_PICKER_STEP,
    Math.min(endRaw, DATE_TIME_PICKER_MAX_MINUTES)
  );
  if (clampedEnd <= clampedStart) return null;
  return {
    startMinutes: clampedStart,
    endMinutes: clampedEnd,
  };
};

export const getSelectableStartWindow = ({
  minMinutes = 0,
  maxMinutes = DATE_TIME_PICKER_MAX_MINUTES,
  durationMinutes = 0,
  stepMinutes = DATE_TIME_PICKER_STEP,
} = {}) => {
  const safeStep = Number.isFinite(stepMinutes) && stepMinutes > 0 ? stepMinutes : 1;
  const safeMin = Number.isFinite(minMinutes) ? minMinutes : 0;
  const safeMax = Number.isFinite(maxMinutes) ? maxMinutes : DATE_TIME_PICKER_MAX_MINUTES;
  const normalizedMin = Math.max(0, Math.min(safeMin, DATE_TIME_PICKER_MAX_MINUTES));
  const normalizedMax = Math.max(
    normalizedMin,
    Math.min(safeMax, DATE_TIME_PICKER_MAX_MINUTES)
  );
  const safeDuration = Number.isFinite(durationMinutes)
    ? Math.max(0, Math.round(durationMinutes))
    : 0;

  const latestStartRaw = normalizedMax - safeDuration;
  const latestStartClamped = Math.max(0, latestStartRaw);
  const latestStartStepped = Math.floor(latestStartClamped / safeStep) * safeStep;
  const selectableMaxMinutes = Math.min(normalizedMax, latestStartStepped);

  return {
    selectableMinMinutes: normalizedMin,
    selectableMaxMinutes,
    hasValidSlot: selectableMaxMinutes >= normalizedMin,
  };
};

export const formatLocationAddress = (location) => {
  const parts = [];
  const address = (location.address ?? "").trim();
  const normalizedAddress = normalizeAddressValue(address);
  if (address) {
    parts.push(address);
  }

  const addIfMissing = (value) => {
    const trimmed = `${value ?? ""}`.trim();
    if (!trimmed) return;
    const normalized = normalizeAddressValue(trimmed);
    if (normalizedAddress && normalizedAddress.includes(normalized)) return;
    if (parts.some((part) => normalizeAddressValue(part).includes(normalized))) {
      return;
    }
    parts.push(trimmed);
  };

  addIfMissing(location.postalCode);
  addIfMissing(location.city);
  return parts.join(", ");
};

const locationUtils = {
  formatLocationAddress,
  buildLocationCoords,
  getBlockedDateSet,
  parseWorkingHours,
  getSelectableStartWindow,
  isBlockedDate,
};

export default locationUtils;
